import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, Star } from 'lucide-react';
import axios from 'axios';
import { useT } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const tr = useT();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  if (testimonials.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-cream" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
            {tr('Voices from the Path')}
          </div>
          <h2 className="text-3xl md:text-4xl font-heading tracking-tight leading-snug text-charcoal-800 mb-4">
            {tr('Transformative Journeys')}
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-charcoal-600 max-w-3xl mx-auto">
            {tr('Hear from seekers who have walked with us on their spiritual path.')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.slice(0, 6).map((t, index) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 p-8 relative hover:shadow-[0_12px_40px_rgba(44,30,22,0.12)] transition-all duration-300"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="absolute top-6 right-6 text-saffron-100" size={40} />
              <div className="flex gap-1 mb-4">
                {[...Array(t.rating || 5)].map((_, i) => (
                  <Star key={i} className="text-gold-500 fill-gold-500" size={16} />
                ))}
              </div>
              <p className="text-base leading-relaxed text-charcoal-700 mb-6 italic relative z-10">
                "{tr(t.quote)}"
              </p>
              <div className="flex items-center gap-4 pt-4 border-t border-charcoal-100">
                {t.image_url ? (
                  <img
                    src={t.image_url}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-saffron-100 flex items-center justify-center text-saffron-600 font-heading text-xl font-bold">
                    {t.name.charAt(0)}
                  </div>
                )}
                <div>
                  <div className="font-body font-semibold text-charcoal-800">{tr(t.name)}</div>
                  <div className="text-xs text-charcoal-500">{tr(t.role)}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
